/**
 * Discovery Report PDF - individual section renderers (Phase 2 modular refactor).
 *
 * Each render function takes (doc, ctx, report, engagement) and mutates ctx.yPos,
 * returning the new yPos so the orchestrator in pdf-generator.js can compose
 * sections in any order dictated by the audience preset.
 *
 * All rendering behavior is preserved byte-for-byte from the pre-refactor
 * generatePDFReport() body; only entry/exit scaffolding has been added.
 */
import autoTable from 'jspdf-autotable';
import { SECTION_CONFIG, COMPONENT_LABELS, LAYER_LABELS } from '@/config/domain-weights';

// Small helpers factored out per-renderer keep each function self-contained.
// We deliberately do NOT share a mutable ctx.yPos object across helpers because
// the extracted bodies still mutate a local `let yPos` variable.
const makeHelpers = (doc, ctx) => {
  const { margin, pageWidth, pageHeight, COLORS } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };
  const drawLine = (y, color = COLORS.border) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };
  return { yPos, addNewPageIfNeeded, drawLine, setYPos: (v) => { yPos = v; } };
};

// getScoreColor is used by Cover + Tech Stack. Kept here so we don't circular-import from pdf-generator.
const _getScoreColor = (COLORS, score) => {
  if (score >= 85) return COLORS.success;
  if (score >= 65) return [14, 165, 233];
  if (score >= 40) return COLORS.warning;
  return COLORS.danger;
};


// ============================================================================
// Cover / Decision View - always on
// ============================================================================
export const renderCover = (doc, ctx, report, engagement) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const drawLine = (y, color = COLORS.border) => {
    doc.setDrawColor(...color); doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };
  const getScoreColor = (s) => _getScoreColor(COLORS, s);
  const getEnhancedBandColor = (score) => {
    if (score >= 85) return COLORS.success;
    if (score >= 70) return [234, 179, 8];
    if (score >= 50) return [249, 115, 22];
    if (score >= 30) return COLORS.danger;
    return COLORS.critical;
  };
  // ===== PAGE 1: COVER PAGE =====
  // Pull branding (consultancy default + per-engagement client logo)
  const branding = report._report_metadata?.branding || {};
  const consultancyName = branding.consultancy_name || 'DefencIQ';
  const consultancyLogo = branding.consultancy_logo_base64 || null;
  const clientLogo = branding.client_logo_base64 || null;

  // Helper to safely render an embedded base64 image without crashing the PDF
  const drawImageSafe = (base64, x, y, maxW, maxH) => {
    if (!base64 || typeof base64 !== 'string') return false;
    try {
      // Detect format from data URI prefix
      let format = 'PNG';
      if (base64.startsWith('data:image/jpeg') || base64.startsWith('data:image/jpg')) format = 'JPEG';
      else if (base64.startsWith('data:image/webp')) format = 'WEBP';
      // SVG via doc.addImage isn't supported by jsPDF Helvetica - skip silently
      if (base64.startsWith('data:image/svg')) return false;
      const props = doc.getImageProperties(base64);
      const ratio = props.width / props.height;
      let w = maxW;
      let h = maxW / ratio;
      if (h > maxH) { h = maxH; w = maxH * ratio; }
      doc.addImage(base64, format, x, y, w, h);
      return true;
    } catch (_e) {
      return false;
    }
  };

  // Header bar (consultancy strip)
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 45, 'F');

  // Consultancy logo (left) + name fallback
  let consultancyLogoDrawn = false;
  if (consultancyLogo) {
    consultancyLogoDrawn = drawImageSafe(consultancyLogo, margin, 10, 50, 25);
  }
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(consultancyLogoDrawn ? 11 : 24);
  doc.setFont('helvetica', 'bold');
  if (!consultancyLogoDrawn) {
    doc.text(consultancyName, margin, 25);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Essential Eight ML2 Discovery Assessment', margin, 35);
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Prepared by ${consultancyName}`, margin, 40);
  }

  // Client logo (top-right)
  if (clientLogo) {
    drawImageSafe(clientLogo, pageWidth - margin - 40, 8, 40, 28);
  }

  // Client info
  yPos = 60;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(engagement.client_name || 'Client Assessment', margin, yPos);

  yPos += 8;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text('DISP Discovery & Readiness Report', margin, yPos);

  yPos += 12;
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.secondary);
  doc.text(`Assessment ID: ${engagement.assessment_id || 'N/A'}`, margin, yPos);
  yPos += 6;
  doc.text(`Assessment Date: ${engagement.assessment_date || new Date().toISOString().split('T')[0]}`, margin, yPos);
  yPos += 6;
  doc.text(`Assessor: ${engagement.assessor_name || 'N/A'}`, margin, yPos);
  yPos += 6;
  // Engagement type - prefer the option label from the question config; fall back to title-casing
  const ENGAGEMENT_TYPE_LABELS = {
    discovery_only: 'Discovery Only',
    snapshot_assessment: 'Snapshot Assessment',
    managed_compliance_onboarding: 'Managed Compliance Onboarding',
    greenfield_ml2_build: 'Greenfield ML2 Build',
    disp_readiness_uplift: 'DISP Readiness Uplift',
  };
  const engTypeLabel = engagement.engagement_type
    ? (ENGAGEMENT_TYPE_LABELS[engagement.engagement_type]
       || engagement.engagement_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    : 'Discovery';
  doc.text(`Engagement Type: ${engTypeLabel}`, margin, yPos);

  // Overall Score Box - Now shows WEIGHTED ML2 Score (compact)
  yPos += 14;
  const scoreBoxWidth = 55;
  const scoreBoxHeight = 32;
  const scoreBoxX = margin;
  
  // Get enhanced scoring data
  const enhancedScores = report._report_metadata?.enhanced_scores || {};
  const weightedScore = enhancedScores.finalScore ?? (report.derived_outputs?.overall_ml2_readiness_score || 0);
  const scoreBand = enhancedScores.scoreBand || { label: 'Calculating...' };
  
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(scoreBoxX, yPos, scoreBoxWidth, scoreBoxHeight, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(scoreBoxX, yPos, scoreBoxWidth, scoreBoxHeight, 2, 2, 'S');

  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('WEIGHTED ML2 SCORE', scoreBoxX + 4, yPos + 6);
  
  doc.setTextColor(...getEnhancedBandColor(weightedScore));
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(`${weightedScore}%`, scoreBoxX + 4, yPos + 22);

  // Status Box - Shows new band label (compact)
  const statusBoxX = scoreBoxX + scoreBoxWidth + 6;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(statusBoxX, yPos, scoreBoxWidth, scoreBoxHeight, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(statusBoxX, yPos, scoreBoxWidth, scoreBoxHeight, 2, 2, 'S');

  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('SCORE BAND', statusBoxX + 4, yPos + 6);
  
  doc.setTextColor(...getEnhancedBandColor(weightedScore));
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(scoreBand.label || 'Critical', statusBoxX + 4, yPos + 18);

  // Show multipliers if applied
  const multipliers = enhancedScores.multipliers || {};
  let multiplierText = '';
  if (multipliers.policyEnforcement < 1) {
    multiplierText += `Policy x${multipliers.policyEnforcement.toFixed(2)} `;
  }
  if (multipliers.evidence < 1) {
    multiplierText += `Evidence x${multipliers.evidence.toFixed(2)}`;
  }
  
  if (multiplierText) {
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`Multipliers: ${multiplierText}`, statusBoxX + 4, yPos + 27);
  }

  // Classification
  yPos += scoreBoxHeight + 8;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Environment Type', margin, yPos);
  
  yPos += 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  // Use new environment type fields
  const environmentType = report.derived_outputs?.environment_type || 'fragmented_adhoc';
  const environmentTypeInfo = report.derived_outputs?.environment_type_info || {};
  
  // Color coding based on environment type
  const envTypeColors = {
    greenfield: COLORS.danger,
    fragmented_adhoc: [234, 179, 8], // amber
    structured_non_compliant: [14, 165, 233], // blue
    mature_aligned: COLORS.success
  };
  
  doc.setTextColor(...(envTypeColors[environmentType] || COLORS.secondary));
  doc.text(environmentTypeInfo.label || 'Unknown', margin, yPos);
  
  yPos += 7;
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(10);
  doc.text(environmentTypeInfo.description || '', margin, yPos);

  // Service Tier Recommendation
  yPos += 15;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommended Service', margin, yPos);
  
  yPos += 10;
  const serviceTierInfo = report.derived_outputs?.service_tier_info || {};
  doc.setTextColor(...COLORS.accent);
  doc.setFontSize(12);
  doc.text(serviceTierInfo.label || 'Snapshot Assessment', margin, yPos);
  
  yPos += 7;
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(10);
  doc.text(serviceTierInfo.description || '', margin, yPos);

  // ===== COVER: DISP SUBMISSION DECISION BANNER + KPI STRIP (from hybrid_report) =====
  const hybridSnapshot = report._report_metadata?.hybrid_report?.executive_snapshot;
  const hybridDecision = report._report_metadata?.hybrid_report?.decision_view;

  if (hybridDecision || hybridSnapshot) {
    // Pin the decision banner + KPI strip to the bottom of the cover page so it never
    // collides with the auto page footer regardless of how much content sits above.
    // Total stack: banner(12) + gap(4) + KPI cards(18) + bottom margin to footer line(12)
    const stackHeight = (hybridDecision?.final_recommendation ? 12 + 4 : 0) + (hybridSnapshot ? 18 : 0);
    const footerLineY = pageHeight - 22; // footer divider sits here
    yPos = Math.max(yPos + 8, footerLineY - stackHeight - 4);

    // Decision banner
    if (hybridDecision?.final_recommendation) {
      const decision = hybridDecision.final_recommendation;
      const bannerColor =
        decision === 'Ready for Submission' ? COLORS.success :
        decision === 'Submit with Conditions' ? [234, 179, 8] :
        COLORS.danger;
      doc.setFillColor(...bannerColor);
      doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`DISP SUBMISSION DECISION: ${decision.toUpperCase()}`, margin + 4, yPos + 8);
      yPos += 16;
    }

    // 4-cell KPI strip from executive_snapshot
    if (hybridSnapshot) {
      const cellW = (contentWidth - 9) / 4;
      const cellH = 16;
      const cells = [
        { label: 'STATUS', value: hybridSnapshot.submission_status || '-' },
        { label: 'CONFIDENCE', value: hybridSnapshot.submission_confidence || '-' },
        { label: 'WEIGHTED ML2', value: `${hybridSnapshot.weighted_ml2_score ?? 0}%` },
        { label: 'TIME TO READY', value: hybridSnapshot.estimated_time_to_readiness || '-' },
      ];
      cells.forEach((c, i) => {
        const x = margin + i * (cellW + 3);
        doc.setFillColor(...COLORS.light);
        doc.roundedRect(x, yPos, cellW, cellH, 1.5, 1.5, 'F');
        doc.setDrawColor(...COLORS.border);
        doc.roundedRect(x, yPos, cellW, cellH, 1.5, 1.5, 'S');
        doc.setTextColor(...COLORS.secondary);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(c.label, x + 3, yPos + 5);
        doc.setTextColor(...COLORS.primary);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const lines = doc.splitTextToSize(String(c.value), cellW - 6);
        doc.text(lines[0] || '', x + 3, yPos + 12);
      });
      yPos += cellH + 2;
    }
  }

  // Cover footer line (above the auto page-number footer)
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Prepared by ${consultancyName}`, margin, pageHeight - 17);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}`,
    pageWidth - margin, pageHeight - 17, { align: 'right' }
  );
  doc.setFontSize(7);
  doc.text('Confidential - For intended recipient use only.', pageWidth / 2, pageHeight - 4, { align: 'center' });
  return yPos;
};

// ============================================================================
// Evidence Readiness Assessment
// ============================================================================
export const renderEvidenceReadinessSection = (doc, ctx, report) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const evidenceReadiness = report._report_metadata?.evidence_readiness_assessment;
  if (!evidenceReadiness) return yPos;
    doc.addPage();
    yPos = margin;

    // Section header banner
    doc.setFillColor(...COLORS.primary);
    doc.rect(margin, yPos, contentWidth, 10, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('EVIDENCE READINESS', margin + 5, yPos + 7);
    yPos += 18;

    if (!evidenceReadiness.assessed) {
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Evidence Readiness: Not Assessed', margin, yPos);
      yPos += 8;
    } else {
      // Overall score + band
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Overall Evidence Readiness', margin, yPos);
      yPos += 7;

      doc.setFontSize(22);
      doc.setTextColor(...COLORS.primary);
      doc.text(`${evidenceReadiness.overall_percent}%`, margin, yPos + 2);
      const bandColor = evidenceReadiness.band.color === 'emerald' ? COLORS.success
        : evidenceReadiness.band.color === 'sky' ? [14, 165, 233]
        : evidenceReadiness.band.color === 'amber' ? COLORS.warning
        : COLORS.danger;
      doc.setFillColor(...bandColor);
      const bandLabel = (evidenceReadiness.band.label || '').toUpperCase();
      const bandW = doc.getTextWidth(bandLabel) + 12;
      doc.roundedRect(margin + 32, yPos - 5, bandW, 8, 2, 2, 'F');
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(bandLabel, margin + 38, yPos);
      yPos += 10;

      // Counts row
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.secondary);
      const countLine = `Available: ${evidenceReadiness.counts.available}   |   Partial: ${evidenceReadiness.counts.partial}   |   Missing: ${evidenceReadiness.counts.missing}`;
      doc.text(countLine, margin, yPos);
      yPos += 10;

      // Per-domain
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Evidence Readiness by DISP Domain', margin, yPos);
      yPos += 5;

      const perDomainRows = evidenceReadiness.per_domain.map(d => [
        d.label,
        d.percent === null ? 'Not Assessed' : `${d.percent}%`,
        d.band?.label || '-',
        `${d.counts.available} / ${d.counts.partial} / ${d.counts.missing}`,
      ]);
      autoTable(doc, {
        startY: yPos,
        head: [['Domain', 'Score', 'Band', 'Available / Partial / Missing']],
        body: perDomainRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: COLORS.light },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 55, halign: 'center' },
        },
      });
      yPos = doc.lastAutoTable.finalY + 10;

      // Evidence Gaps
      if (evidenceReadiness.gaps.by_domain.length > 0) {
        if (yPos > pageHeight - 60) { doc.addPage(); yPos = margin; }
        doc.setTextColor(...COLORS.primary);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Evidence Gaps', margin, yPos);
        yPos += 7;

        evidenceReadiness.gaps.by_domain.forEach(d => {
          if (yPos > pageHeight - 40) { doc.addPage(); yPos = margin; }
          doc.setTextColor(...COLORS.primary);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(d.label, margin, yPos);
          yPos += 5;
          if (d.not_available.length > 0) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(...COLORS.danger);
            doc.text('Not Available:', margin + 3, yPos);
            yPos += 4.5;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.secondary);
            d.not_available.forEach(name => {
              if (yPos > pageHeight - 20) { doc.addPage(); yPos = margin; }
              doc.text(`- ${name}`, margin + 7, yPos);
              yPos += 4.5;
            });
          }
          if (d.partially_available.length > 0) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(...COLORS.warning);
            doc.text('Partially Available:', margin + 3, yPos);
            yPos += 4.5;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.secondary);
            d.partially_available.forEach(name => {
              if (yPos > pageHeight - 20) { doc.addPage(); yPos = margin; }
              doc.text(`- ${name}`, margin + 7, yPos);
              yPos += 4.5;
            });
          }
          yPos += 3;
        });
      }

      // Narrative
      if (yPos > pageHeight - 50) { doc.addPage(); yPos = margin; }
      doc.setFillColor(241, 245, 249);
      const narrativeLines = doc.splitTextToSize(evidenceReadiness.narrative, contentWidth - 8);
      const narrativeH = narrativeLines.length * 4.5 + 8;
      doc.roundedRect(margin, yPos, contentWidth, narrativeH, 2, 2, 'F');
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(narrativeLines, margin + 4, yPos + 6);
      yPos += narrativeH + 8;
    }

    // Accelerator handoff (always shown)
    if (yPos > pageHeight - 40) { doc.addPage(); yPos = margin; }
    doc.setFillColor(238, 242, 255);
    doc.roundedRect(margin, yPos, contentWidth, 24, 2, 2, 'F');
    doc.setTextColor(49, 46, 129);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Next Step - Evidence Validation', margin + 4, yPos + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const handoffLines = doc.splitTextToSize(
      'Evidence identified during Discovery should be collected, reviewed and mapped to applicable DISP requirements within DefencIQ Accelerator. Accelerator provides detailed artefact management, evidence validation and submission-readiness tracking.',
      contentWidth - 8
    );
    doc.text(handoffLines, margin + 4, yPos + 11);
    yPos += 30;
  return yPos;
};

// ============================================================================
// DISP Gap Analysis
// ============================================================================
export const renderDispGapAnalysis = (doc, ctx, report) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const drawLine = (y, color = COLORS.border) => {
    doc.setDrawColor(...color); doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };
  const dispGapAnalysis = report._report_metadata?.disp_gap_analysis || [];
  if (dispGapAnalysis.length === 0) return yPos;
    addNewPageIfNeeded(60);
    yPos += 10;

    // Section header
    doc.setFillColor(127, 29, 29); // red-900
    doc.rect(margin, yPos, contentWidth, 10, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DISP GAP ANALYSIS', margin + 5, yPos + 7);
    yPos += 14;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    doc.text('Defence-aligned, audit-ready control assessment grouped by DISP domain', margin, yPos);
    yPos += 8;

    dispGapAnalysis.forEach((domain) => {
      // Domain header
      addNewPageIfNeeded(30);
      doc.setFillColor(241, 245, 249); // slate-100
      doc.rect(margin, yPos, contentWidth, 8, 'F');
      doc.setDrawColor(...COLORS.border);
      doc.rect(margin, yPos, contentWidth, 8, 'S');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text(`${domain.label.toUpperCase()}  (${domain.controls.length} controls)`, margin + 4, yPos + 5.5);
      yPos += 12;

      domain.controls.forEach((ctrl, ci) => {
        // Estimate space needed for this control block
        const isFail = ctrl.compliance_status === 'non_compliant';
        const isPartial = ctrl.compliance_status === 'partially_compliant';
        const needsFinding = isFail || isPartial;
        const blockHeight = 30 + (needsFinding ? 25 : 0);
        addNewPageIfNeeded(blockHeight);

        // Control name + compliance badge
        const badgeText = isFail ? 'NON-COMPLIANT' : isPartial ? 'PARTIALLY COMPLIANT' : 'COMPLIANT';
        const badgeColor = isFail ? COLORS.danger : isPartial ? COLORS.warning : COLORS.success;

        // Left border accent
        doc.setFillColor(...badgeColor);
        doc.rect(margin, yPos, 1.5, 6, 'F');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.primary);
        doc.text(ctrl.short_name, margin + 4, yPos + 4.5);

        if (ctrl.is_gating) {
          const nameWidth = doc.getTextWidth(ctrl.short_name);
          doc.setFillColor(...COLORS.danger);
          doc.roundedRect(margin + 5 + nameWidth, yPos + 0.5, 14, 5, 1, 1, 'F');
          doc.setFontSize(5.5);
          doc.setTextColor(...COLORS.white);
          doc.text('GATING', margin + 5 + nameWidth + 2, yPos + 4);
        }

        // Badge on right
        doc.setFillColor(...badgeColor);
        const bw = doc.getTextWidth(badgeText) * 0.6 + 6;
        doc.roundedRect(pageWidth - margin - bw, yPos + 0.5, bw, 5, 1, 1, 'F');
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.white);
        doc.text(badgeText, pageWidth - margin - bw + 3, yPos + 4);
        yPos += 9;

        // Control count summary for grouped controls
        if (ctrl.control_count > 1) {
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          let summary = `${ctrl.control_count} controls assessed`;
          if (ctrl.fail_count > 0) summary += ` | ${ctrl.fail_count} failed`;
          if (ctrl.partial_count > 0) summary += ` | ${ctrl.partial_count} partial`;
          doc.text(summary, margin + 4, yPos);
          yPos += 4;
        }

        // REQUIREMENT
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139); // slate-500
        doc.text('REQUIREMENT', margin + 4, yPos);
        yPos += 3.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...COLORS.secondary);
        const reqLines = doc.splitTextToSize(ctrl.requirement, contentWidth - 8);
        doc.text(reqLines, margin + 4, yPos);
        yPos += reqLines.length * 3.5 + 2;

        // FINDING (only for non-compliant / partial)
        if (needsFinding) {
          addNewPageIfNeeded(20);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(100, 116, 139);
          doc.text('FINDING', margin + 4, yPos);
          yPos += 3.5;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(...COLORS.secondary);
          const findLines = doc.splitTextToSize(ctrl.finding, contentWidth - 8);
          doc.text(findLines, margin + 4, yPos);
          yPos += findLines.length * 3.5 + 2;

          // RECOMMENDATION
          addNewPageIfNeeded(20);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(100, 116, 139);
          doc.text('RECOMMENDATION', margin + 4, yPos);
          yPos += 3.5;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(...COLORS.secondary);
          const recLines = doc.splitTextToSize(ctrl.recommendation, contentWidth - 8);
          doc.text(recLines, margin + 4, yPos);
          yPos += recLines.length * 3.5 + 2;
        }

        // EVIDENCE REQUIRED
        addNewPageIfNeeded(15);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('EVIDENCE REQUIRED', margin + 4, yPos);
        yPos += 3.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.secondary);
        const evidenceText = ctrl.evidence_required.map(e => '- ' + e).join('\n');
        const evLines = doc.splitTextToSize(evidenceText, contentWidth - 8);
        doc.text(evLines, margin + 4, yPos);
        yPos += evLines.length * 3.2 + 3;

        // Separator between controls
        if (ci < domain.controls.length - 1) {
          drawLine(yPos, COLORS.border);
          yPos += 5;
        }
      });

      yPos += 6;
    });
  return yPos;
};

// ============================================================================
// Technology Stack Status
// ============================================================================
export const renderTechnologyStack = (doc, ctx, report) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  addNewPageIfNeeded(60);

  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TECHNOLOGY STACK STATUS', margin + 5, yPos + 7);
  yPos += 20;

  // Stack Summary Block
  const stackSummary = report._report_metadata?.stack_summary || {};
  const stackAnalysis = report._report_metadata?.stack_analysis || {};

  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Technology Stack Summary', margin, yPos);
  yPos += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);

  const summaryItems = [
    { label: 'Implemented', value: stackSummary.implemented_count || 0, color: COLORS.success },
    { label: 'Needs Improvement', value: stackSummary.needs_improvement_count || 0, color: [234, 88, 12] },
    { label: 'Partially Implemented', value: stackSummary.partial_count || 0, color: COLORS.warning },
    { label: 'Missing', value: stackSummary.missing_count || 0, color: COLORS.danger },
    { label: 'Planned', value: stackSummary.planned_count || 0, color: [148, 163, 184] }
  ];

  summaryItems.forEach(item => {
    doc.setTextColor(...item.color);
    doc.setFont('helvetica', 'bold');
    doc.text(`${item.value}`, margin + 5, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    doc.text(item.label, margin + 15, yPos);
    yPos += 5;
  });

  if (stackAnalysis.stack_alignment_score !== undefined) {
    yPos += 2;
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(8);
    doc.text(`Stack Alignment Score: ${stackAnalysis.stack_alignment_score}% (informational - not part of ML2 weighted score)`, margin + 5, yPos);
    yPos += 8;
  } else {
    yPos += 5;
  }

  // 4-Column Stack Table grouped by layer
  const stackRows = report._report_metadata?.stack_status_rows || [];
  const layerOrder = ['Enforcement Layer', 'Validation Layer', 'Monitoring Layer', 'Compliance Layer'];

  layerOrder.forEach(layerLabel => {
    const layerRows = stackRows.filter(r => r.layer === layerLabel);
    if (layerRows.length === 0) return;

    addNewPageIfNeeded(30);

    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(layerLabel, margin, yPos);
    yPos += 6;

    const tableData = layerRows.map(row => [
      row.component_label,
      row.status,
      row.requirement,
      row.action.length > 55 ? row.action.substring(0, 52) + '...' : row.action
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Component', 'Status', 'Requirement', 'Recommended Action']],
      body: tableData,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 7.5,
        cellPadding: 2.5,
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: {
        fillColor: COLORS.light,
      },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { cellWidth: 56 },
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          // Status column coloring (primary signal)
          if (data.column.index === 1) {
            const val = data.cell.raw;
            if (val === 'Implemented') {
              data.cell.styles.textColor = COLORS.success;
              data.cell.styles.fontStyle = 'bold';
            } else if (val === 'Needs Improvement') {
              data.cell.styles.textColor = [234, 88, 12];
              data.cell.styles.fontStyle = 'bold';
            } else if (val === 'Partially Implemented') {
              data.cell.styles.textColor = COLORS.warning;
            } else if (val === 'Missing') {
              data.cell.styles.textColor = COLORS.danger;
              data.cell.styles.fontStyle = 'bold';
            } else if (val === 'Planned') {
              data.cell.styles.textColor = [148, 163, 184];
            }
          }
          // Requirement column (secondary, muted)
          if (data.column.index === 2) {
            data.cell.styles.textColor = [107, 114, 128];
            data.cell.styles.fontSize = 7;
          }
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 8;
  });
  return yPos;
};

// ============================================================================
// Recommended Next Step (always on)
// ============================================================================
export const renderRecommendedNextStep = (doc, ctx, report, engagement) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const dispReadiness = report._report_metadata?.disp_readiness || {};
  const hybridReport = report._report_metadata?.hybrid_report;
  // ===== RECOMMENDED NEXT STEP (closing) =====
  // Structure per report brief: badge, Current Position, Immediate Priority, Evidence Validation, Recommended Engagement.
  doc.addPage();
  yPos = margin;

  doc.setFillColor(...COLORS.accent);
  doc.rect(margin, yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('RECOMMENDED NEXT STEP', margin + 5, yPos + 7);
  yPos += 18;

  const decisionColorMap = {
    'READY_FOR_SUBMISSION': COLORS.success,
    'PARTIALLY_READY': COLORS.warning,
    'NOT_READY': COLORS.danger,
  };
  const decisionLabelMap = {
    'READY_FOR_SUBMISSION': 'READY FOR SUBMISSION',
    'PARTIALLY_READY': 'PARTIALLY READY',
    'NOT_READY': 'DO NOT SUBMIT',
  };
  const decisionKey = dispReadiness.submission_status || 'NOT_READY';
  const badgeColor = decisionColorMap[decisionKey] || COLORS.danger;
  const badgeLabel = decisionLabelMap[decisionKey] || 'DO NOT SUBMIT';
  doc.setFillColor(...badgeColor);
  const bW = doc.getTextWidth(badgeLabel) + 14;
  doc.roundedRect(margin, yPos, bW, 9, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(badgeLabel, margin + 7, yPos + 6);
  yPos += 15;

  const gatingFailCount = report._report_metadata?.gating_status?.score0Count ?? 0;
  const gatingPartialCount = report._report_metadata?.gating_status?.score1Count ?? 0;
  const timeToReady = dispReadiness.time_to_readiness || hybridReport?.executive_snapshot?.estimated_time_to_readiness || 'Not yet estimated';
  const recommendedService = engagement.recommended_service || report._report_metadata?.recommended_service || 'DefencIQ ML2 Uplift Program';
  const isReadyForSubmission = decisionKey === 'READY_FOR_SUBMISSION';

  const drawParagraph = (heading, body) => {
    addNewPageIfNeeded(30);
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.text(heading, margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.secondary);
    const lines = doc.splitTextToSize(body, contentWidth);
    doc.text(lines, margin, yPos);
    yPos += lines.length * 4.6 + 6;
  };

  const currentPosition = isReadyForSubmission
    ? `${engagement.client_name || 'The organisation'} has demonstrated sufficient control implementation and evidence coverage to be considered ready for DISP CSQ submission at this time.`
    : `${engagement.client_name || 'The organisation'} is not yet in a position to submit the DISP Cyber Security Questionnaire. ${gatingFailCount} gating control${gatingFailCount === 1 ? '' : 's'} remain unimplemented and ${gatingPartialCount} are only partially in place. Estimated time to readiness is ${timeToReady}.`;
  drawParagraph('Current Position', currentPosition);

  const immediatePriority = isReadyForSubmission
    ? 'Focus should now shift to evidence validation, CSQ response preparation, and finalising documentation to support submission through DefencIQ Accelerator.'
    : 'Priority remediation must address gating-control failures and critical high-priority gaps first. These are the items that block CSQ submission regardless of maturity elsewhere in the environment. Phase 1 of the roadmap describes the specific workstreams to be delivered.';
  drawParagraph('Immediate Priority', immediatePriority);

  drawParagraph(
    'Evidence Validation',
    'Evidence identified during Discovery should be collected, reviewed and mapped to applicable DISP requirements. Detailed artefact management, control mapping and CSQ submission preparation is delivered through DefencIQ Accelerator.'
  );

  drawParagraph(
    'Recommended Engagement',
    isReadyForSubmission
      ? `DefencIQ recommends progressing to ${recommendedService} to finalise evidence validation and CSQ preparation through DefencIQ Accelerator.`
      : `DefencIQ recommends progressing to a structured DISP uplift program (${recommendedService}) to remediate critical and gating control gaps, followed by evidence validation and CSQ preparation through DefencIQ Accelerator.`
  );
  return yPos;
};

// ============================================================================
// Technical Summary - CES / PGS / CMS breakdown, Audit Failure Risk,
// Score Adjustments Applied, Key Findings, Weakest Domains.
// Auditor / Assessor preset only.
// ============================================================================
export const renderTechnicalSummary = (doc, ctx, report) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const getScoreColor = (s) => _getScoreColor(COLORS, s);
  const getEnhancedBandColor = (score) => {
    if (score >= 85) return COLORS.success;
    if (score >= 70) return [234, 179, 8];
    if (score >= 50) return [249, 115, 22];
    if (score >= 30) return COLORS.danger;
    return COLORS.critical;
  };
  const getFlagSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return COLORS.critical;
      case 'high': return [249, 115, 22];
      case 'medium': return [234, 179, 8];
      default: return COLORS.secondary;
    }
  };
  const drawLine = (y, color = COLORS.border) => {
    doc.setDrawColor(...color); doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };
  doc.addPage();
  yPos = margin;

  // Section Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TECHNICAL SUMMARY', margin + 5, yPos + 7);
  yPos += 20;

  const enhancedScores = report._report_metadata?.enhanced_scores || {};
  const domainFindings = report._report_metadata?.domain_findings || [];

  // ===== ENHANCED SCORING SECTION =====
  // Group Scores (CES/PGS/CMS)
  const groupScores = enhancedScores.groupScores || {};
  const flags = enhancedScores.flags || [];
  
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Scoring Component Breakdown', margin, yPos);
  yPos += 8;
  
  // Draw three score boxes for CES, PGS, CMS
  const groupBoxWidth = (contentWidth - 10) / 3;
  const groupBoxHeight = 35;
  
  const groupsData = [
    { key: 'CES', name: 'Control Effectiveness', weight: '50%', score: groupScores.CES || 0 },
    { key: 'PGS', name: 'Policy & Governance', weight: '30%', score: groupScores.PGS || 0 },
    { key: 'CMS', name: 'Compliance Maturity', weight: '20%', score: groupScores.CMS || 0 }
  ];
  
  groupsData.forEach((group, idx) => {
    const boxX = margin + (idx * (groupBoxWidth + 5));
    
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(boxX, yPos, groupBoxWidth, groupBoxHeight, 2, 2, 'F');
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(boxX, yPos, groupBoxWidth, groupBoxHeight, 2, 2, 'S');
    
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${group.key} (${group.weight})`, boxX + 3, yPos + 8);
    
    doc.setTextColor(...getEnhancedBandColor(group.score));
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${group.score}%`, boxX + 3, yPos + 22);
    
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(group.name, boxX + 3, yPos + 30);
  });
  
  yPos += groupBoxHeight + 10;
  
  // Detection Flags
  if (flags.length > 0) {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Detection Alerts', margin, yPos);
    yPos += 8;
    
    flags.forEach(flag => {
      const flagColor = getFlagSeverityColor(flag.severity);
      
      // Draw flag indicator
      doc.setFillColor(...flagColor);
      doc.circle(margin + 3, yPos - 1, 2, 'F');
      
      doc.setTextColor(...flagColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(flag.label, margin + 8, yPos);
      
      yPos += 5;
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(flag.description, margin + 8, yPos);
      yPos += 7;
    });
    
    yPos += 5;
  }
  
  // Audit Failure Likelihood Section
  const auditLikelihood = enhancedScores.auditFailureLikelihood;
  const auditReasons = enhancedScores.auditFailureReasons || [];
  const auditMessage = enhancedScores.auditFailureMessage;
  
  if (auditLikelihood) {
    // Get color based on likelihood
    const likelihoodColors = {
      'LOW': COLORS.success,
      'MODERATE': [234, 179, 8],      // yellow
      'HIGH': [249, 115, 22],          // orange
      'CRITICAL': COLORS.critical
    };
    const likelihoodColor = likelihoodColors[auditLikelihood] || COLORS.secondary;
    
    // Draw section box for CRITICAL/HIGH
    if (auditLikelihood === 'CRITICAL' || auditLikelihood === 'HIGH') {
      const boxHeight = 25 + (auditReasons.length * 5);
      doc.setFillColor(...(auditLikelihood === 'CRITICAL' ? [254, 242, 242] : [255, 247, 237]));
      doc.setDrawColor(...likelihoodColor);
      doc.roundedRect(margin, yPos, contentWidth, boxHeight, 2, 2, 'FD');
      yPos += 5;
    }
    
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Audit Failure Risk Assessment', margin + 5, yPos);
    
    // Badge
    doc.setFillColor(...likelihoodColor);
    doc.roundedRect(margin + contentWidth - 35, yPos - 5, 30, 8, 2, 2, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(7);
    doc.text(auditLikelihood, margin + contentWidth - 33, yPos);
    
    yPos += 8;
    
    // Message
    doc.setTextColor(...likelihoodColor);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const messageLines = doc.splitTextToSize(auditMessage, contentWidth - 10);
    doc.text(messageLines, margin + 5, yPos);
    yPos += messageLines.length * 4 + 3;
    
    // Reasons
    if (auditReasons.length > 0) {
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(8);
      auditReasons.forEach(reason => {
        doc.setFillColor(...COLORS.secondary);
        doc.circle(margin + 8, yPos - 1, 0.8, 'F');
        doc.text(reason, margin + 12, yPos);
        yPos += 4;
      });
    }
    
    yPos += 8;
  }
  
  // Multipliers Summary (if any applied)
  const multipliers = enhancedScores.multipliers || {};
  if (multipliers.policyEnforcement < 1 || multipliers.evidence < 1 || multipliers.confidence < 1) {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Score Adjustments Applied', margin, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.secondary);
    
    if (multipliers.policyEnforcement < 1) {
      doc.setFillColor(...COLORS.secondary);
      doc.circle(margin + 6, yPos - 1, 0.8, 'F');
      doc.text(`Policy Enforcement: x${multipliers.policyEnforcement.toFixed(2)} (weak technical enforcement)`, margin + 10, yPos);
      yPos += 5;
    }
    if (multipliers.evidence < 1) {
      doc.setFillColor(...COLORS.secondary);
      doc.circle(margin + 6, yPos - 1, 0.8, 'F');
      doc.text(`Evidence Quality: x${multipliers.evidence.toFixed(2)} (insufficient audit evidence)`, margin + 10, yPos);
      yPos += 5;
    }
    if (multipliers.confidence < 1) {
      doc.setFillColor(...COLORS.secondary);
      doc.circle(margin + 6, yPos - 1, 0.8, 'F');
      doc.text(`Answer Confidence: x${multipliers.confidence.toFixed(2)} (${multipliers.unknownCount || 0} unknown answers)`, margin + 10, yPos);
      yPos += 5;
    }
    yPos += 5;
  }

  // Key findings
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Key Findings', margin, yPos);
  yPos += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.secondary);

  const overallScore = report.derived_outputs?.overall_ml2_readiness_score || 0;
  const weightedScore = enhancedScores.finalScore ?? overallScore;
  const scoreBand = enhancedScores.scoreBand || { label: 'Critical' };
  const environmentTypeInfo = report.derived_outputs?.environment_type || {};
  const serviceTierInfo = report.derived_outputs?.recommended_service || {};
  const findings = [
    `Weighted ML2 score: ${weightedScore}% (${scoreBand.label || 'Critical'})`,
    `Base domain score: ${overallScore}%`,
    `Environment type: ${environmentTypeInfo.label || 'Unknown'}`,
    `Recommended service: ${serviceTierInfo.label || 'Snapshot Assessment'}`,
    `Gating controls passed: ${report._report_metadata?.gating_control_details?.filter(c => c.passed).length || 0} of ${report._report_metadata?.gating_control_details?.length || 0}`,
    `Priority gaps identified: ${report.derived_outputs?.priority_gaps?.length || 0}`
  ];

  findings.forEach(finding => {
    doc.setFillColor(...COLORS.secondary);
    doc.circle(margin + 6, yPos - 1, 0.8, 'F');
    doc.text(finding, margin + 10, yPos);
    yPos += 6;
  });

  // Weakest Domains
  yPos += 10;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Top 3 Weakest Domains', margin, yPos);
  yPos += 8;

  const weakestDomains = [...domainFindings]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  weakestDomains.forEach((domain, idx) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...getScoreColor(domain.score));
    doc.text(`${idx + 1}. ${domain.domain_name}: ${domain.score}%`, margin + 5, yPos);
    yPos += 6;
  });
  return yPos;
};

// ============================================================================
// CSQ Submission Mapping - per-category submission guidance, Ready-to-Submit
// responses, Evidence for Submission list, Minimum Viable Submission Path.
// Auditor / Assessor preset only.
// ============================================================================
export const renderCsqSubmissionMapping = (doc, ctx, report) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;
  let yPos = ctx.yPos;
  const addNewPageIfNeeded = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) { doc.addPage(); yPos = margin; return true; }
    return false;
  };
  const drawLine = (y, color = COLORS.border) => {
    doc.setDrawColor(...color); doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };
  const csqMappingData = report._report_metadata?.csq_mapping;
  const csqDomains = csqMappingData?.domains || [];
  if (csqDomains.length > 0) {
    addNewPageIfNeeded(60);
    yPos += 5;

    // Section header
    doc.setFillColor(49, 46, 129); // indigo-900
    doc.rect(margin, yPos, contentWidth, 10, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CSQ SUBMISSION MAPPING', margin + 5, yPos + 7);
    yPos += 14;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    doc.text('Submission-ready guidance and copy-paste answers for the DISP Cyber Security Questionnaire', margin, yPos);
    yPos += 8;

    // Submission Confidence Banner
    const conf = csqMappingData.submission_confidence;
    const confColor = conf === 'LOW' ? COLORS.danger : conf === 'MODERATE' ? COLORS.warning : COLORS.success;
    doc.setFillColor(conf === 'LOW' ? 254 : conf === 'MODERATE' ? 255 : 240, conf === 'LOW' ? 242 : conf === 'MODERATE' ? 251 : 253, conf === 'LOW' ? 242 : conf === 'MODERATE' ? 235 : 244);
    doc.roundedRect(margin, yPos, contentWidth, 14, 2, 2, 'F');
    doc.setDrawColor(...confColor);
    doc.roundedRect(margin, yPos, contentWidth, 14, 2, 2, 'S');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('SUBMISSION CONFIDENCE:', margin + 4, yPos + 4.5);
    doc.setFontSize(9);
    doc.setTextColor(...confColor);
    doc.text(conf, margin + 48, yPos + 4.5);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    const confLines = doc.splitTextToSize(csqMappingData.confidence_explanation, contentWidth - 8);
    doc.text(confLines, margin + 4, yPos + 9);
    yPos += 18;

    csqDomains.forEach((domain) => {
      addNewPageIfNeeded(25);
      // Domain header
      doc.setFillColor(238, 242, 255);
      doc.rect(margin, yPos, contentWidth, 7, 'F');
      doc.setDrawColor(199, 210, 254);
      doc.rect(margin, yPos, contentWidth, 7, 'S');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(49, 46, 129);
      doc.text(`${domain.domain_label.toUpperCase()}  (${domain.blocks.length} ${domain.blocks.length === 1 ? 'category' : 'categories'})`, margin + 4, yPos + 5);
      yPos += 11;

      domain.blocks.forEach((block, bi) => {
        addNewPageIfNeeded(55);

        // Category label + Response badge
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(79, 70, 229);
        doc.text(block.csq_category.toUpperCase(), margin + 2, yPos);
        const respColor = block.response_direction === 'No' ? COLORS.danger : block.response_direction === 'Partial' ? COLORS.warning : COLORS.success;
        const respText = `CSQ Response: ${block.response_direction}`;
        doc.setFontSize(6);
        const respW = doc.getTextWidth(respText) + 6;
        doc.setFillColor(...respColor);
        doc.roundedRect(pageWidth - margin - respW, yPos - 3.5, respW, 5, 1, 1, 'F');
        doc.setTextColor(...COLORS.white);
        doc.text(respText, pageWidth - margin - respW + 3, yPos);
        yPos += 4;

        // Control name
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.primary);
        const nameLines = doc.splitTextToSize(block.combined_name, contentWidth - 4);
        doc.text(nameLines, margin + 2, yPos);
        yPos += nameLines.length * 4 + 2;

        // Submission Guidance
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('SUBMISSION GUIDANCE', margin + 2, yPos);
        yPos += 3.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...COLORS.secondary);
        const guidanceLines = doc.splitTextToSize(block.submission_guidance, contentWidth - 4);
        doc.text(guidanceLines, margin + 2, yPos);
        yPos += guidanceLines.length * 3.5 + 2;

        // CSQ Response (Ready to Submit)
        addNewPageIfNeeded(20);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(49, 46, 129);
        doc.text('CSQ RESPONSE (READY TO SUBMIT)', margin + 2, yPos);
        yPos += 3.5;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7.5);
        doc.setTextColor(...COLORS.secondary);
        const respLines = doc.splitTextToSize(block.csq_response_text, contentWidth - 4);
        doc.text(respLines, margin + 2, yPos);
        yPos += respLines.length * 3.5 + 2;

        // Supporting Evidence with Defence context
        addNewPageIfNeeded(15);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('SUPPORTING EVIDENCE FOR SUBMISSION', margin + 2, yPos);
        yPos += 3.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.secondary);
        const evItems = (block.supporting_evidence || []).map(e => {
          const item = typeof e === 'string' ? e : e.item;
          return '- ' + item;
        });
        const evText = evItems.join('\n');
        const evLines = doc.splitTextToSize(evText, contentWidth - 4);
        doc.text(evLines, margin + 2, yPos);
        yPos += evLines.length * 3.2 + 2;

        // Minimum Viable Submission Path
        if (block.minimum_viable_path && block.minimum_viable_path.length > 0) {
          addNewPageIfNeeded(15);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(49, 46, 129);
          doc.text('MINIMUM VIABLE SUBMISSION PATH', margin + 2, yPos);
          yPos += 3.5;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(...COLORS.secondary);
          block.minimum_viable_path.forEach((step, si) => {
            const stepText = `${si + 1}. ${step}`;
            const stepLines = doc.splitTextToSize(stepText, contentWidth - 6);
            doc.text(stepLines, margin + 4, yPos);
            yPos += stepLines.length * 3.2 + 1;
          });
          yPos += 1;
        }

        // Submission Decision
        addNewPageIfNeeded(10);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...respColor);
        const decLines = doc.splitTextToSize(block.submission_decision, contentWidth - 4);
        doc.text(decLines, margin + 2, yPos);
        yPos += decLines.length * 3.5 + 3;

        // Separator
        if (bi < domain.blocks.length - 1) {
          drawLine(yPos, COLORS.border);
          yPos += 4;
        }
      });

      yPos += 6;
    });

    // (CSQ.2 Overall CSQ Submission Insight removed - covered by HY.8 + HY.11)
    // (CSQ.3 Minimum Path to Submission Readiness removed - covered by HY.8 numbered path)
  }

  // (GC.1 Gating Controls Status table removed - covered by HY.6 Gating Control Assessment)

  // ===== PRIORITY GAPS =====
  addNewPageIfNeeded(50);

  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Priority Gaps', margin, yPos);
  yPos += 8;

  const priorityGaps = report.derived_outputs?.priority_gaps || [];
  const gapsTableData = priorityGaps.slice(0, 15).map(g => [
    g.gap.length > 55 ? g.gap.substring(0, 52) + '...' : g.gap,
    SECTION_CONFIG[g.domain]?.title?.split(' ')[0] || g.domain,
    g.priority.toUpperCase()
  ]);

  if (gapsTableData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Gap', 'Domain', 'Priority']],
      body: gapsTableData,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: COLORS.light,
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.column.index === 2 && data.section === 'body') {
          const value = data.cell.raw;
          if (value === 'CRITICAL') {
            data.cell.styles.textColor = COLORS.danger;
            data.cell.styles.fontStyle = 'bold';
          } else if (value === 'HIGH') {
            data.cell.styles.textColor = COLORS.warning;
            data.cell.styles.fontStyle = 'bold';
          } else if (value === 'MEDIUM') {
            data.cell.styles.textColor = [14, 165, 233];
          }
        }
      },
    });
    yPos = doc.lastAutoTable.finalY + 15;
  }
  return yPos;
};


// ============================================================================
// Assessor Signature Block - formal sign-off page.
// Auditor / Assessor preset only. Renders labeled blank lines for physical
// or digital signature. Uses no dynamic data (Option A: blank lines only).
// ============================================================================
export const renderAssessorSignature = (doc, ctx, report, engagement) => {
  const { COLORS, margin, contentWidth, pageWidth, pageHeight } = ctx;

  doc.addPage();
  let yPos = margin;

  // Header banner
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('ASSESSOR ATTESTATION', margin + 5, yPos + 7);
  yPos += 18;

  // Intro / context line
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const clientName = engagement?.client_name || 'the client';
  const contextLine = `The assessor named below confirms responsibility for the findings recorded in this Discovery report for ${clientName}.`;
  const contextLines = doc.splitTextToSize(contextLine, contentWidth);
  doc.text(contextLines, margin, yPos);
  yPos += contextLines.length * 4.6 + 6;

  // Attestation statement (boxed)
  const statement = 'I confirm that the findings recorded above reflect the evidence reviewed during this engagement and have been prepared in accordance with the Essential Eight Maturity Model (ML2) assessment methodology and DISP CSQ requirements applicable at the time of assessment.';
  const stmtLines = doc.splitTextToSize(statement, contentWidth - 10);
  const stmtHeight = stmtLines.length * 4.6 + 10;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos, contentWidth, stmtHeight, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, yPos, contentWidth, stmtHeight, 2, 2, 'S');

  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Statement of Attestation', margin + 5, yPos + 6);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.secondary);
  doc.text(stmtLines, margin + 5, yPos + 12);
  yPos += stmtHeight + 12;

  // Signature block header
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(10.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Assessor Details', margin, yPos);
  yPos += 8;

  // Helper: draw a labeled signature line
  const drawSignatureLine = (label, opts = {}) => {
    const lineY = yPos + 8;
    const labelY = lineY + 4.5;

    // The signature line itself
    doc.setDrawColor(...COLORS.secondary);
    doc.setLineWidth(0.4);
    doc.line(margin, lineY, margin + (opts.width || contentWidth), lineY);

    // Label below line
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(label, margin, labelY);

    yPos = labelY + 8;
  };

  // Full-width signature lines
  drawSignatureLine('Assessor Name');
  drawSignatureLine('Title / Role');
  drawSignatureLine('Organisation');

  // Signature line - full width, taller gap to allow for ink signature
  const sigLineY = yPos + 14;
  doc.setDrawColor(...COLORS.secondary);
  doc.setLineWidth(0.4);
  doc.line(margin, sigLineY, margin + contentWidth, sigLineY);
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Signature', margin, sigLineY + 4.5);
  yPos = sigLineY + 12;

  // Two side-by-side lines: Date + Engagement Reference
  const halfWidth = (contentWidth - 8) / 2;
  const rowLineY = yPos + 8;
  const rowLabelY = rowLineY + 4.5;

  doc.setDrawColor(...COLORS.secondary);
  doc.setLineWidth(0.4);
  doc.line(margin, rowLineY, margin + halfWidth, rowLineY);
  doc.line(margin + halfWidth + 8, rowLineY, margin + contentWidth, rowLineY);

  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Date of Attestation', margin, rowLabelY);
  doc.text('Engagement Reference / Scope', margin + halfWidth + 8, rowLabelY);
  yPos = rowLabelY + 12;

  // Footnote - methodology reference
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.secondary);
  const footnote = 'Methodology reference: ACSC Essential Eight Maturity Model (ML2) and Defence Industry Security Program (DISP) CSQ. This attestation applies to the point-in-time evidence reviewed during this Discovery engagement.';
  const footLines = doc.splitTextToSize(footnote, contentWidth);
  doc.text(footLines, margin, yPos);
  yPos += footLines.length * 3.8 + 4;

  return yPos;
};

// ============================================================================
// Auditor Metadata Overlay - dedicated page 2 for Auditor / Assessor preset.
// Provides an at-a-glance context sheet: assessment metadata (auto-filled from
// engagement) + assessor identification (blank) + methodology reference.
// ============================================================================
export const renderAuditorMetadata = (doc, ctx, report, engagement) => {
  const { COLORS, margin, contentWidth, pageWidth } = ctx;

  doc.addPage();
  let yPos = margin;

  // Header banner
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('ASSESSMENT CONTEXT & ASSESSOR DETAILS', margin + 5, yPos + 7);
  yPos += 16;

  // Sub-caption
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'This page establishes the context, scope and methodology under which this Discovery assessment was performed.',
    margin, yPos
  );
  yPos += 10;

  // ---- Block 1: Assessment Metadata (auto-filled) ----
  const drawSectionTitle = (title) => {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, yPos);
    yPos += 5;
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 4;
  };

  const drawKV = (rows, opts = {}) => {
    // Two columns of key/value pairs. Each row is 9mm tall (blank underline
    // rows need more vertical breathing room than pure label/value rows).
    const colGap = 8;
    const colW = (contentWidth - colGap) / 2;
    const rowH = 9;
    rows.forEach((pair, i) => {
      const col = i % 2;
      const rowIdx = Math.floor(i / 2);
      const x = margin + col * (colW + colGap);
      const y = yPos + rowIdx * rowH;

      // Key label
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(pair.key.toUpperCase(), x, y);

      // Value (auto-filled OR blank underline)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (pair.value) {
        doc.setTextColor(...COLORS.primary);
        const truncated = doc.splitTextToSize(String(pair.value), colW)[0] || '';
        doc.text(truncated, x, y + 4);
      } else {
        // Blank line for assessor to write on
        doc.setDrawColor(...COLORS.secondary);
        doc.setLineWidth(0.3);
        doc.line(x, y + 4.2, x + colW - 2, y + 4.2);
      }
    });
    const rowsUsed = Math.ceil(rows.length / 2);
    yPos += rowsUsed * rowH + 6;
  };

  drawSectionTitle('Assessment Metadata');
  const assessmentMeta = [
    { key: 'Client Organisation', value: engagement?.client_name || null },
    { key: 'Assessment Date', value: engagement?.assessment_date || new Date().toISOString().split('T')[0] },
    { key: 'Assessment ID', value: engagement?.assessment_id || null },
    { key: 'Engagement Type', value: engagement?.engagement_type
        ? String(engagement.engagement_type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'DISP Discovery Assessment' },
    { key: 'Report Version', value: report?._report_metadata?.report_version || '1.0' },
    { key: 'Status', value: 'Point-in-time Discovery' },
  ];
  drawKV(assessmentMeta);

  // ---- Block 2: Assessor Identification (blank fields for auditor to fill) ----
  drawSectionTitle('Assessor Identification');
  const assessorFields = [
    { key: 'Assessor Name', value: engagement?.assessor_name || null },
    { key: 'Assessor Organisation', value: null },
    { key: 'Engagement Reference / Scope', value: null },
    { key: 'Report Reviewed By', value: null },
  ];
  drawKV(assessorFields);

  // ---- Block 3: Methodology Reference ----
  drawSectionTitle('Methodology & Scope');
  const methodLines = [
    { label: 'Framework', body: 'Australian Cyber Security Centre (ACSC) Essential Eight Maturity Model, targeted at Maturity Level Two (ML2).' },
    { label: 'Standard', body: 'Defence Industry Security Program (DISP) Cyber Security Questionnaire (CSQ) submission requirements applicable at the time of assessment.' },
    { label: 'Approach', body: 'Discovery-based control and evidence review. Findings reflect the point-in-time state of implemented controls, evidence coverage and gating-control status as declared by the client and observed during the engagement.' },
    { label: 'Scope Limitation', body: 'This report does not constitute a formal audit or certification. It is intended to support DISP CSQ preparation, executive decision-making and remediation planning.' },
  ];

  doc.setFontSize(8.5);
  methodLines.forEach(({ label, body }) => {
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, margin, yPos);

    doc.setTextColor(...COLORS.secondary);
    doc.setFont('helvetica', 'normal');
    const labelW = doc.getTextWidth(`${label}:`) + 2;
    const lines = doc.splitTextToSize(body, contentWidth - labelW);
    doc.text(lines[0] || '', margin + labelW, yPos);
    if (lines.length > 1) {
      const rest = doc.splitTextToSize(body, contentWidth);
      // Re-split full body against full width for subsequent lines
      const remaining = rest.slice(1);
      remaining.forEach((line, li) => {
        doc.text(line, margin, yPos + 4 + li * 4);
      });
      yPos += 4 + remaining.length * 4 + 3;
    } else {
      yPos += 6;
    }
  });

  // NB: do NOT call doc.addPage() here. The next section
  // (renderHybridSections for Executive Snapshot) unconditionally starts a
  // new page (startsOnNewPage default = true), so a trailing addPage() here
  // produces a blank page. See iteration_45 blank-page regression.
  return yPos;
};